import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox } from '@els/els-react--checkbox';
import { Pill } from '@els/els-react--pill';
import { Button } from '@els/els-react--button';
import { TextInput } from '@els/els-react--text-input';
import { historyReducerKey } from '../../Constants';
import { handleSearchInput, onDeleteHistoryRecord, toggleSelectAllOnPage } from '../../HistoryAction';
import { getHistorySelectionKey } from '../../utils';
import ConfirmationPopup from '../../ConfirmationPopup';
import { trackHistoryDeleteButtonClick, trackHistoryMiniSearchStart } from '../../analytics';

import './HistoryToolbar.scss';

const SEARCH_DEBOUNCE_MS = 800;

function HistoryToolbar({ page, paginatedItems, allSelectedOnPage }) {
  const dispatch = useDispatch();
  const { selectedItems: selectedItemsRaw, searchInputText } = useSelector((state) => state.get(historyReducerKey));
  const selectedItems = Array.isArray(selectedItemsRaw) ? selectedItemsRaw : [];
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  // Local mirror so typing stays responsive; the redux dispatch (which triggers the search API) is debounced.
  const [searchValue, setSearchValue] = useState(searchInputText);
  const isRecentPage = page === 'recent' || page === 'Recent';
  const searchInputDescriptionId = 'history-toolbar-search-history-description';
  const getSearchLabel = (count) => (count === 1 ? 'search' : 'searches');
  const selectedCount = allSelectedOnPage ? paginatedItems.length : selectedItems.length;
  const toolbarRegionLabel = 'Search actions';

  // Sync local value when search text changes externally (e.g. cleared on tab switch).
  useEffect(() => {
    setSearchValue(searchInputText);
  }, [searchInputText]);

  const commitSearch = useMemo(
    () => (query) => {
      if (query.trim()) {
        trackHistoryMiniSearchStart(page);
      }
      dispatch(handleSearchInput(query));
    },
    [dispatch, page]
  );

  // Long enough that a typed word is not searched letter-by-letter; Enter commits immediately.
  const debouncedSearch = useMemo(() => debounce(commitSearch, SEARCH_DEBOUNCE_MS), [commitSearch]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    // Inside a form this would submit and reload the page.
    event.preventDefault();
    debouncedSearch.cancel();
    commitSearch(event.target.value || '');
  };

  const handleDeleteClick = () => {
    trackHistoryDeleteButtonClick(page);

    // If allSelectedOnPage, delete all items on page, else delete selectedItems
    let items;
    if (allSelectedOnPage) {
      items = paginatedItems.map(({ item, groupItemIndex, itemIndex, groupItem }) => ({ item, groupItemIndex, itemIndex, groupItem }));
    } else {
      // Only delete selected items that are on this page
      items = paginatedItems.filter(({ item }) => selectedItems.includes(getHistorySelectionKey(item)));
    }
    setItemsToDelete(items);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    // Always produce the required format for payloadforDelete
    let payloadforDelete;
    if (page.toLowerCase() === 'recent') {
      payloadforDelete = itemsToDelete.map(({ item }) => item);
    } else {
      payloadforDelete = itemsToDelete.map(({ item }) => ({
        resultName: item.resultName,
        databaseId: item.databaseId,
      }));
    }

    dispatch(onDeleteHistoryRecord(payloadforDelete, page));
    setShowDeleteConfirm(false);
    setItemsToDelete([]);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemsToDelete([]);
  };

  return (
    <>
      <section className={cn('history-toolbar', 'u-els-display-flex')} aria-label={toolbarRegionLabel}>
        <div className="history-toolbar__left u-els-display-flex">
          <div>
            <Checkbox
              data-e2e="history-select-all"
              id={`history-select-all-${page}`}
              label={`Select all ${page} searches`}
              shouldHideLabel
              checked={allSelectedOnPage}
              aria-label={`Select all ${page} searches`}
              indeterminate={selectedItems.length > 0 && !allSelectedOnPage}
              onChange={() => {
                const selectionKeys = paginatedItems.map(({ item }) => getHistorySelectionKey(item));
                dispatch(toggleSelectAllOnPage(selectionKeys, !allSelectedOnPage));
              }}
            />
          </div>
          <div className="u-els-margin-left-1o2">
            <Pill className="u-els-margin-right-1o2" aria-hidden="true">{`${selectedItems.length}`}</Pill>
            <span aria-hidden="true">Selected</span>
          </div>
          <div className="u-els-margin-top-1o8">
            {(() => {
              const deleteLabel = allSelectedOnPage
                ? `all ${page} searches on this page`
                : `${selectedCount} selected ${page} ${getSearchLabel(selectedCount)}`;
              return (
                <Button
                  type="link"
                  className="u-els-margin-left"
                  data-e2e="auto-save-delete"
                  id="auto-save-delete"
                  aria-label={`Delete ${deleteLabel}`}
                  onClick={handleDeleteClick}
                  disabled={selectedItems.length === 0 && !allSelectedOnPage}>
                  Delete
                </Button>
              );
            })()}
          </div>
        </div>
        <div className="history-toolbar__right">
          {isRecentPage && (
            <span id={searchInputDescriptionId} className="sr-only">
              Results filter shortly after you stop typing. Press Enter to search immediately.
            </span>
          )}
          <TextInput
            data-e2e="search-history-input"
            type="search"
            aria-label={isRecentPage ? 'Search for keywords within recent history' : `Search ${page} history`}
            aria-describedby={isRecentPage ? searchInputDescriptionId : undefined}
            value={searchValue}
            iconRight={{ sprite: 'Search', children: 'Search' }}
            className="history-toolbar__right-input"
            label="Search history"
            placeholder={`Search for keywords within ${page} queries`}
            onChange={(e) => {
              const query = e?.target?.value || '';
              setSearchValue(query);
              debouncedSearch(query);
            }}
            onKeyDown={handleSearchKeyDown}
            shouldHideLabel
          />
        </div>
      </section>
      {showDeleteConfirm && (
        <ConfirmationPopup
          title="Confirm deletion"
          text={`Deleting ${itemsToDelete.length} ${page.toLowerCase()} ${getSearchLabel(itemsToDelete.length)} is a destructive action and cannot be undone. Do you wish to proceed?`}
          submitButtonText="Yes, delete permanently"
          submitButtonType="warn"
          dismissButtonText="Cancel"
          actionButtonSize="medium"
          isCancelButton
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          closed={false}
          selfClose={null}
        />
      )}
    </>
  );
}

HistoryToolbar.propTypes = {
  page: PropTypes.string.isRequired,
  paginatedItems: PropTypes.array.isRequired,
  allSelectedOnPage: PropTypes.bool.isRequired,
};

export default HistoryToolbar;
